/**
 * Patient Link Router
 * 
 * Main entry point for all patient action links (/p/:token/*).
 * Handles OTP verification and routes to the appropriate action component.
 */

import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { OTPGate } from '@/components/patient-links';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AlertCircle, Calendar, FileText, FlaskConical, CreditCard, Video, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BookingWidget from '@/components/patient-booking/BookingWidget';
import LabResultsViewer from '@/components/patient-labs/LabResultsViewer';
import LabOrderPortal from '@/components/patient-labs/LabOrderPortal';
import PatientInvoicesPortal from '@/components/patient-portal/PatientInvoicesPortal';

interface PatientActionLink {
  id: string;
  purpose: string;
  clinic: {
    id: string;
    name: string;
  };
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

const PatientLinkRouter: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [isVerified, setIsVerified] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [linkData, setLinkData] = useState<PatientActionLink | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md border-slate-700 bg-slate-800 text-white">
          <CardHeader className="text-center">
            <AlertCircle className="mx-auto h-16 w-16 text-red-500" />
            <CardTitle className="mt-4 text-xl text-red-500">Invalid Link</CardTitle>
            <CardDescription className="text-slate-400">
              This link appears to be malformed. Please check the URL and try again.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleVerified = (newSessionToken: string, newPatientId: string, newLinkData: PatientActionLink) => {
    setSessionToken(newSessionToken);
    setPatientId(newPatientId);
    setLinkData(newLinkData);
    setIsVerified(true);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Show OTP gate if not verified
  if (!isVerified) {
    return (
      <OTPGate
        token={token}
        onVerified={handleVerified}
        onError={handleError}
      />
    );
  }

  // Route to appropriate action based on purpose
  if (linkData) {
    switch (linkData.purpose) {
      case 'intake':
      case 'intake_form':
        return (
          <PatientActionPlaceholder
            icon={<FileText className="h-12 w-12" />}
            title="Patient Intake Forms"
            description="Complete your intake forms before your appointment"
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      case 'appointment_booking':
      case 'appointment_reschedule':
        return (
          <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
            <div className="w-full max-w-lg">
              <div className="text-center mb-6">
                <img src="/novamed_logo_white.svg" alt="NovaMed" className="mx-auto h-8 mb-2" />
                <p className="text-slate-400 text-sm">{linkData.clinic.name}</p>
              </div>
              <BookingWidget
                sessionToken={sessionToken!}
                clinicId={linkData.clinic.id}
                clinicName={linkData.clinic.name}
                patientId={patientId!}
                patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
              />
            </div>
          </div>
        );
      
      case 'appointment_cancel':
        return (
          <PatientActionPlaceholder
            icon={<Calendar className="h-12 w-12" />}
            title="Cancel Appointment"
            description="Cancel your upcoming appointment"
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      case 'lab_order':
        return (
          <LabOrderPortal
            sessionToken={sessionToken!}
            orderId={linkData.metadata?.orderId || linkData.related_entity_id}
            patientId={patientId!}
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      case 'lab_results':
        return (
          <LabResultsViewer
            sessionToken={sessionToken!}
            patientId={patientId!}
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      case 'consent_form':
      case 'consent_sign':
        return (
          <PatientActionPlaceholder
            icon={<FileText className="h-12 w-12" />}
            title="Sign Consent Forms"
            description="Review and sign required consent forms"
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      case 'payment':
      case 'billing':
      case 'patient_portal':
        return (
          <PatientInvoicesPortal
            sessionToken={sessionToken!}
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      case 'telehealth_join':
        return (
          <PatientActionPlaceholder
            icon={<Video className="h-12 w-12" />}
            title="Join Telehealth Visit"
            description="Join your video appointment"
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      case 'chat':
        return (
          <PatientActionPlaceholder
            icon={<MessageCircle className="h-12 w-12" />}
            title="Secure Messaging"
            description="Message your healthcare provider"
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
      
      default:
        return (
          <PatientActionPlaceholder
            icon={<FileText className="h-12 w-12" />}
            title="Patient Action"
            description="Complete your requested action"
            clinicName={linkData.clinic.name}
            patientName={linkData.patient ? `${linkData.patient.first_name} ${linkData.patient.last_name}` : undefined}
          />
        );
    }
  }

  return null;
};

// Placeholder component for patient actions that haven't been fully implemented yet
interface PatientActionPlaceholderProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  clinicName: string;
  patientName?: string;
}

const PatientActionPlaceholder: React.FC<PatientActionPlaceholderProps> = ({
  icon,
  title,
  description,
  clinicName,
  patientName,
}) => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-lg border-slate-700 bg-slate-800 text-white">
        <CardHeader className="text-center">
          <img src="/novamed_logo_white.svg" alt="NovaMed" className="mx-auto h-8 mb-4" />
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand-teal/20 text-brand-teal">
            {icon}
          </div>
          <CardTitle className="mt-4 text-2xl">{title}</CardTitle>
          <CardDescription className="text-slate-400">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Clinic</span>
              <span className="font-medium">{clinicName}</span>
            </div>
            {patientName && (
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-400">Patient</span>
                <span className="font-medium">{patientName}</span>
              </div>
            )}
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
            <p className="text-sm text-amber-300">
              <AlertCircle className="inline-block h-4 w-4 mr-1" />
              This feature is coming soon. The full patient action flow will be available in an upcoming release.
            </p>
          </div>
          <Button className="w-full bg-brand-teal hover:bg-brand-teal/80">
            Continue
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientLinkRouter;



